import { DocumentTemplateType } from '../../api-client/autogenerated';

/**
 * A package holds documents of another type, so it is not something to pick on its own -- it goes
 * wherever the documents it holds go. Maps each package type to the type that carries it.
 *
 * Kept apart from DocumentTypeTreeView so it stays importable without pulling in scripts/utils.
 */
export const PACKAGE_DOCUMENT_TYPE_CARRIERS: Partial<
  Record<DocumentTemplateType, DocumentTemplateType>
> = {
  [DocumentTemplateType.SubmittalPackages]: DocumentTemplateType.Submittals,
  [DocumentTemplateType.CloseoutSubmittalPackages]: DocumentTemplateType.CloseoutSubmittals,
  [DocumentTemplateType.AsBuiltPackages]: DocumentTemplateType.AsBuilt,
};

export const PACKAGE_DOCUMENT_TYPES = Object.keys(
  PACKAGE_DOCUMENT_TYPE_CARRIERS,
) as DocumentTemplateType[];

/**
 * A short, human-readable account of what an export contains, for listing past exports. Package
 * types are not counted: they follow their carrier and were never picked on their own.
 */
export const describeArchiveSelection = (
  selection: {
    documentTypes?: DocumentTemplateType[];
    includeDesignFiles?: boolean;
    includeEmails?: boolean;
  },
  totalSelectableDocumentTypes: number,
): string => {
  const { documentTypes, includeDesignFiles, includeEmails } = selection;
  // Exports taken before the picker existed carry no selection and contain everything.
  if (!documentTypes) return 'Everything';

  const pickedCount = documentTypes.filter((type) => !PACKAGE_DOCUMENT_TYPES.includes(type)).length;
  if (pickedCount >= totalSelectableDocumentTypes && includeDesignFiles && includeEmails) {
    return 'Everything';
  }

  const parts: string[] = [];
  if (pickedCount > 0) parts.push(`${pickedCount} document type${pickedCount === 1 ? '' : 's'}`);
  if (includeDesignFiles) parts.push('CENTERLINE Drive');
  if (includeEmails) parts.push('emails');
  return parts.length > 0 ? parts.join(', ') : 'Nothing';
};

/** Adds each package type whose carrier is selected, and drops the ones whose carrier is not. */
export const withCarriedPackageDocumentTypes = (documentTypes: DocumentTemplateType[]) => {
  const withoutPackages = documentTypes.filter((type) => !PACKAGE_DOCUMENT_TYPES.includes(type));
  return [
    ...withoutPackages,
    ...PACKAGE_DOCUMENT_TYPES.filter((packageType) =>
      withoutPackages.includes(PACKAGE_DOCUMENT_TYPE_CARRIERS[packageType]!),
    ),
  ];
};
